import { useLayoutEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils";

export type DotRingProps = {
  /** How many dots are lit, counted clockwise from the top. */
  filled: number;
  /** How many dots the ring has. */
  total: number;
  /** Dot radius, on the ring's own 100-unit grid. */
  dot?: number;
  /** On a solid primary card the dots take the card's text color. */
  tone?: "card" | "primary";
  /** What sits in the middle: a figure, an icon, a photo. */
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

/**
 * Rounded, because `Math.cos` is allowed to differ between engines and does: the server rendered
 * 15.528000059645983 where the browser computed 15.52800005964599, React called that a hydration
 * mismatch and threw the whole page away and re-rendered it from scratch. Three decimals on a
 * 100-unit viewBox is finer than a pixel, so nothing moves.
 */
const round = (value: number) => Math.round(value * 1000) / 1000;

/** How far the dots sit from the middle, on the 100-unit grid. */
const RADIUS = 45;

/** Air kept between the dots and what sits inside them, on the same grid. */
const GAP = 4;

/** How much of a line box the glyphs fill: digits and capitals stop well short of its edges. */
const INK = 0.78;

/**
 * How far from (x, y) the content reaches: the farthest corner of any line of text or any image.
 * Lines count from their glyphs, not their line box, and a round photo counts as a circle, so
 * only what is actually painted can push into the dots.
 */
const reachOf = (body: HTMLElement, x: number, y: number) => {
  let reach = 0;
  const corner = (rect: DOMRect, height: number) => {
    const middle = rect.top + rect.height / 2;
    const dx = Math.max(Math.abs(rect.left - x), Math.abs(rect.right - x));
    const dy = Math.max(Math.abs(middle - height / 2 - y), Math.abs(middle + height / 2 - y));
    reach = Math.max(reach, Math.hypot(dx, dy));
  };

  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent?.trim() || !node.parentElement) continue;
    const fontSize = parseFloat(getComputedStyle(node.parentElement).fontSize);
    range.selectNodeContents(node);
    for (const rect of range.getClientRects()) corner(rect, Math.min(rect.height, fontSize * INK));
  }

  for (const atom of body.querySelectorAll<HTMLElement | SVGElement>("img, svg, video, canvas")) {
    const rect = atom.getBoundingClientRect();
    const circular = parseFloat(getComputedStyle(atom).borderTopLeftRadius) >= rect.width / 2;
    if (circular) {
      const centre = Math.hypot(rect.left + rect.width / 2 - x, rect.top + rect.height / 2 - y);
      reach = Math.max(reach, centre + rect.width / 2);
    } else corner(rect, rect.height);
  }

  return reach;
};

/**
 * A ring of dots with the first few lit. The site's one chart: a countdown, a share of a week,
 * a streak. Size it with a `size-*` class; the dots scale with it.
 *
 * What sits in the middle never touches the dots. Size it to fit, but a longer figure, a longer
 * translation or a narrow screen can still outgrow the ring, so the ring measures it after layout
 * and shrinks it, as one piece, until it clears the dots by `GAP`.
 */
export const DotRing = ({ filled, total, dot = 2.4, tone = "card", children, className, style }: DotRingProps) => {
  const ringRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const lit = Math.max(0, Math.min(total, Math.round(filled)));
  const on = tone === "primary" ? "var(--primary-foreground)" : "var(--primary)";
  const off = tone === "primary" ? "var(--site-on-primary-dot)" : "var(--site-dot)";
  const hasBody = children !== undefined && children !== null && children !== false;

  useLayoutEffect(() => {
    const ring = ringRef.current;
    const body = bodyRef.current;
    if (!ring || !body) return;

    const fit = () => {
      body.style.scale = "";
      const box = ring.getBoundingClientRect();
      if (!box.width) return;
      const room = (box.width / 100) * (RADIUS - dot - GAP);
      const reach = reachOf(body, box.left + box.width / 2, box.top + box.height / 2);
      if (reach > room) body.style.scale = String(room / reach);
    };

    fit();
    // Scale does not change layout size, so watching the body cannot loop.
    const observer = new ResizeObserver(fit);
    observer.observe(ring);
    observer.observe(body);
    let live = true;
    void document.fonts?.ready.then(() => live && fit());
    return () => {
      live = false;
      observer.disconnect();
    };
  }, [dot, hasBody]);

  return (
    <div ref={ringRef} className={cn("relative grid shrink-0 place-items-center", className)} style={style}>
      <svg viewBox="0 0 100 100" aria-hidden className="absolute inset-0 size-full">
        {Array.from({ length: total }, (_, index) => {
          const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
          return (
            <circle
              key={index}
              cx={round(50 + Math.cos(angle) * RADIUS)}
              cy={round(50 + Math.sin(angle) * RADIUS)}
              r={dot}
              style={{ fill: index < lit ? on : off }}
            />
          );
        })}
      </svg>
      {hasBody ? (
        <div ref={bodyRef} className="relative grid place-items-center">
          {children}
        </div>
      ) : null}
    </div>
  );
};

/** Lit dots for a share: 0.5 of 24 dots lights 12. Anything above zero lights at least one. */
export const litDots = (share: number, total: number) => {
  if (!Number.isFinite(share) || share <= 0) return 0;
  return Math.max(1, Math.min(total, Math.round(share * total)));
};
